import React, { type ReactNode, useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { Panel } from '@atlaskit/navigation-system/layout/panel';
import { PanelSplitter } from '@atlaskit/navigation-system/layout/panel-splitter';
import { Root } from '@atlaskit/navigation-system/layout/root';
import Suspense from '@atlaskit/react-ufo/suspense';

import type { AnyGlobalRootQueryData, GlobalRootProps } from '../common/types';
import { AsyncIntlProvider } from '../common/utils/locale';
import { AppChrome } from './AppChrome';
import { AppChromeSkeleton } from './AppChromeSkeleton';
import { messages } from './messages';

/**
 * GlobalRoot will _progressively_ increase in capability.
 * Initially, it will care about the Banner, TopNav and SideNav.
 *
 * The TopNav slot is backed by GlobalTopNavigation (GTN). Consumers customize
 * it by providing a `TopNavSlot` render prop and passing GTN props (such as
 * `AppHomeSlot` and `queryRef`) to the supplied `TopNav` component.
 *
 * Help and Chat temporarily share the Navigation System end panel. Their
 * separate callbacks feed a single coordinator so opening either surface
 * replaces the other without coupling their public slot APIs.
 */
export function GlobalRoot<Q extends AnyGlobalRootQueryData = AnyGlobalRootQueryData>(
	props: GlobalRootProps<Q>,
): React.JSX.Element {
	const { intlMode = 'async' } = props;

	return (
		<AsyncIntlProvider disabled={intlMode === 'inherit'}>
			<GlobalRootInner {...props} />
		</AsyncIntlProvider>
	);
}

// Keep all useIntl calls below the provider, including when no host provider exists.
function GlobalRootInner<Q extends AnyGlobalRootQueryData>(
	props: GlobalRootProps<Q>,
): React.JSX.Element {
	const { formatMessage } = useIntl();
	const {
		app,
		appLabel,
		children,
		customizationStore,
		intlMode,
		isAnonymous = false,
		isChromeless = false,
		networkStorageParams,
		BannerSlot,
		SideNavSlot,
		TopNavSlot,
		useRelayQuery,
		...rootProps
	} = props;
	const [chatPanelContent, setChatPanelContent] = useState<ReactNode>();
	const [helpPanelContent, setHelpPanelContent] = useState<ReactNode>();
	const closeHelpPanel = useCallback(() => setHelpPanelContent(undefined), []);
	const openHelpPanel = useCallback((content: ReactNode) => {
		setChatPanelContent(undefined);
		setHelpPanelContent(content);
	}, []);
	const isChatPanelOpen = chatPanelContent !== undefined;
	const openChatPanel = useCallback((content: ReactNode) => {
		setHelpPanelContent(undefined);
		setChatPanelContent(content);
	}, []);
	const closeChatPanel = useCallback(() => {
		setChatPanelContent(undefined);
	}, []);

	return (
		<Root {...rootProps}>
			{!isChromeless && (
				// `AppChrome` calls the consumer's `useRelayQuery()`, so it suspends while
				// the app chrome data is in flight. This boundary keeps that wait local to
				// the chrome: without it the suspension unwinds past `Root` to whichever
				// boundary the consumer provides, and `children` — a sibling of
				// `AppChrome` — never commits, blanking the page to load the navigation.
				//
				// UFO's `Suspense` is used over React's so the fallback holds the current
				// interaction open, keeping the chrome's load time inside the measured
				// interaction rather than completing it early against a skeleton.
				<Suspense
					fallback={
						<AppChromeSkeleton hasSideNav={SideNavSlot !== null} hasTopNav={TopNavSlot !== null} />
					}
					interactionName="global-root-app-chrome"
				>
					<AppChrome
						app={app}
						appLabel={appLabel}
						BannerSlot={BannerSlot}
						closeChatPanel={closeChatPanel}
						closeHelpPanel={closeHelpPanel}
						customizationStore={customizationStore}
						intlMode={intlMode}
						isAnonymous={isAnonymous}
						isChatPanelOpen={isChatPanelOpen}
						isHelpPanelOpen={helpPanelContent !== undefined}
						networkStorageParams={networkStorageParams}
						openChatPanel={openChatPanel}
						openHelpPanel={openHelpPanel}
						SideNavSlot={SideNavSlot}
						TopNavSlot={TopNavSlot}
						useRelayQuery={useRelayQuery}
					/>
				</Suspense>
			)}
			{children}
			{helpPanelContent !== undefined && (
				<Panel label={formatMessage(messages.helpPanel)}>{helpPanelContent}</Panel>
			)}

			{isChatPanelOpen && (
				<Panel label={formatMessage(messages.chatPanel)}>
					{chatPanelContent}
					<PanelSplitter label={formatMessage(messages.resizeChatPanel)} />
				</Panel>
			)}
		</Root>
	);
}
